import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLang } from '../i18n/LanguageContext';
import { LANGS } from '../i18n/types';
import { common } from '../i18n/pages/common';
import { managerProfileText } from '../i18n/pages/managerProfile';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { changeMyPassword, getMyProfile, updateMyProfile } from '../api.managerProfile';
import { clearStoredAuthState } from '../auth';
import AppButton from '../components/AppButton';
import {
  getTtlockAccount, connectTtlockAccount, getTtlockLocks, syncTtlockLocks,
  TtlockAccount, TtlockLock,
} from '../api.ttlock';
import MD5 from 'crypto-js/md5';
import {
  Cleaner, CleanerRequest,
  getCleaners, createCleaner, updateCleaner, deleteCleaner,
} from '../api.cleaners';
import { Apartment, getApartments } from '../api.apartments';
import { Contact, getMyContacts, createContact, updateContact, deleteContact } from '../api.contacts';
import SettingsPaymentMethods from './SettingsPaymentMethods';
import Subscription from './Subscription';
import ChannelManagerHub from '../components/channelManager/ChannelManagerHub';

const getToken = () => localStorage.getItem('token') || '';

type TabKey = 'profile' | 'ttlock' | 'integrations' | 'cleaners' | 'contacts' | 'payment-methods' | 'subscription' | 'privacy';
const VALID_TABS: TabKey[] = ['profile', 'ttlock', 'integrations', 'cleaners', 'contacts', 'payment-methods', 'subscription', 'privacy'];

const ACTIVE_TAB_STORAGE_KEY = 'profile.activeTab';

const formGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: 12,
};

const labelStyle: React.CSSProperties = {
  fontWeight: 600,
  display: 'block',
  marginBottom: 4,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #cbd5e0',
  borderRadius: 8,
  fontSize: 14,
  boxSizing: 'border-box',
};

const emptyCleanerForm = (): CleanerRequest => ({ name: '', scheduleDays: 30, apartmentIds: [] });

const TtlockAccountStatus: React.FC<{ account: TtlockAccount }> = ({ account }) => {
  const { lang } = useLang();
  const t = managerProfileText[lang];
  const expiresAt = account.tokenExpiresAt ? new Date(account.tokenExpiresAt) : null;
  const isExpired = expiresAt ? expiresAt < new Date() : false;
  const daysLeft = expiresAt ? Math.ceil((expiresAt.getTime() - Date.now()) / 86400000) : null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: isExpired ? '#fef2f2' : '#f0fdf4', border: `1px solid ${isExpired ? '#fca5a5' : '#86efac'}`, borderRadius: 8 }}>
      <span style={{ fontSize: '1.1rem' }}>{isExpired ? '⚠️' : '✅'}</span>
      <div>
        <div style={{ fontWeight: 600, color: isExpired ? '#991b1b' : '#166534' }}>{t.ttlockConnectedAs} {account.ttlockUsername}</div>
        {expiresAt && (
          <div style={{ fontSize: '0.8rem', color: isExpired ? '#991b1b' : '#4b7a53' }}>
            {isExpired ? t.ttlockTokenExpired : t.ttlockTokenValid(expiresAt.toLocaleDateString(), daysLeft!)}
          </div>
        )}
      </div>
    </div>
  );
};


const ManagerProfile: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { lang, setLang } = useLang();
  const tc = common[lang];
  const t = managerProfileText[lang];
  const tabLabels: Record<TabKey, string> = {
    profile: `👤 ${t.tabProfile}`,
    ttlock: t.tabTtlock,
    // Not in the i18n bundle: "Channel manager" is the product term and is left untranslated in
    // every language, the same way Hostex and Stripe are.
    integrations: '🔌 Channel manager',
    cleaners: t.tabCleaners,
    contacts: t.tabContacts,
    'payment-methods': t.tabPaymentMethods,
    subscription: t.tabSubscription,
    privacy: t.tabPrivacy,
  };
  // The tab is derived from the URL, never mirrored into state: react-router applies location changes
  // in a transition, so an urgent setActiveTab() plus a fast browser Back left the old panel up with
  // the URL already on the new tab (PR #116 QA). Without a valid ?tab= we fall back to the last tab
  // this browser had open (localStorage), then 'profile'.
  const [fallbackTab] = useState<TabKey>(() => {
    const saved = localStorage.getItem(ACTIVE_TAB_STORAGE_KEY);
    return (saved === 'ttlock' || saved === 'cleaners' || saved === 'contacts' || saved === 'payment-methods' || saved === 'subscription') ? saved : 'profile';
  });
  const fromUrl = searchParams.get('tab') as TabKey;
  const activeTab: TabKey = VALID_TABS.includes(fromUrl) ? fromUrl : fallbackTab;

  // Push (not replace) a history entry per tab switch so browser Back steps through previously viewed tabs.
  const switchTab = (tab: TabKey) => {
    setSearchParams(p => { p.set('tab', tab); return p; });
  };
  const [ttlockLoaded, setTtlockLoaded] = useState(false);
  const [cleanersLoaded, setCleanersLoaded] = useState(false);
  const [contactsLoaded, setContactsLoaded] = useState(false);

  // ── Profile state ────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [ico, setIco] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);

  // ── TTLock state ─────────────────────────────────────────────────────────────
  const [ttlockAccount, setTtlockAccount] = useState<TtlockAccount | null>(null);
  const [ttlockLocks, setTtlockLocks] = useState<TtlockLock[]>([]);
  const [ttlockLoading, setTtlockLoading] = useState(false);
  const [ttlockConnecting, setTtlockConnecting] = useState(false);
  const [ttlockError, setTtlockError] = useState<string | null>(null);
  const [ttlockSuccess, setTtlockSuccess] = useState<string | null>(null);
  const [ttlockUsername, setTtlockUsername] = useState('');
  const [ttlockPassword, setTtlockPassword] = useState('');
  // Collapsed by default only once we know there's already an active connection;
  // kept open while loading / when there is none / when the token is expired.
  const [ttlockReconnectOpen, setTtlockReconnectOpen] = useState(true);
  const ttlockReconnectInitRef = useRef(false);

  // ── Cleaners state ───────────────────────────────────────────────────────────
  const [cleaners, setCleaners] = useState<Cleaner[]>([]);
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [cleanersLoading, setCleanersLoading] = useState(false);
  const [cleanerError, setCleanerError] = useState<string | null>(null);
  const [cleanerSuccess, setCleanerSuccess] = useState<string | null>(null);
  const [showCleanerForm, setShowCleanerForm] = useState(false);
  const [editingCleanerId, setEditingCleanerId] = useState<number | null>(null);
  const [cleanerForm, setCleanerForm] = useState<CleanerRequest>(emptyCleanerForm());
  const [savingCleaner, setSavingCleaner] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  // ── Contacts state ───────────────────────────────────────────────────────────
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactError, setContactError] = useState<string | null>(null);
  const [contactSuccess, setContactSuccess] = useState<string | null>(null);
  const [editingContactId, setEditingContactId] = useState<number | null>(null);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactForm, setContactForm] = useState<Contact>({ name: '', phoneNumber: '', email: '' });
  const [savingContact, setSavingContact] = useState(false);

  const token = useMemo(() => getToken(), []);

  // ── Load profile on mount ────────────────────────────────────────────────────
  const loadProfile = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const profile = await getMyProfile(token);
      setEmail(profile.email || '');
      setFirstName(profile.firstName || '');
      setLastName(profile.lastName || '');
      setPhoneNumber(profile.phoneNumber || '');
      setIco(profile.ico || '');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  // ── Load TTLock lazily on first tab open ─────────────────────────────────────
  const loadTtlock = useCallback(async () => {
    setTtlockLoading(true);
    setTtlockError(null);
    try {
      const account = await getTtlockAccount(token);
      setTtlockAccount(account);
      if (account) {
        const locks = await syncTtlockLocks(token);
        setTtlockLocks(locks);
      }
    } catch (err: any) {
      setTtlockError(err.response?.data?.message || err.message || 'Failed to load TTLock data');
    } finally {
      setTtlockLoading(false);
    }
  }, [token]);

  useEffect(() => {
    localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, activeTab);
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'ttlock' && !ttlockLoaded) {
      setTtlockLoaded(true);
      loadTtlock();
    }
  }, [activeTab, ttlockLoaded, loadTtlock]);

  // Decide the initial collapsed/open state exactly once, after the first load finishes,
  // so later re-renders (e.g. after connecting) don't fight the user's own toggling.
  useEffect(() => {
    if (ttlockLoaded && !ttlockLoading && !ttlockReconnectInitRef.current) {
      ttlockReconnectInitRef.current = true;
      const expiresAt = ttlockAccount?.tokenExpiresAt ? new Date(ttlockAccount.tokenExpiresAt) : null;
      const isExpired = expiresAt ? expiresAt < new Date() : false;
      const hasActiveConnection = !!ttlockAccount && !isExpired;
      setTtlockReconnectOpen(!hasActiveConnection);
    }
  }, [ttlockLoaded, ttlockLoading, ttlockAccount]);

  // ── Load Cleaners lazily on first tab open ───────────────────────────────────
  const loadCleaners = useCallback(async () => {
    setCleanersLoading(true);
    setCleanerError(null);
    try {
      const [cl, apts] = await Promise.all([getCleaners(token), getApartments(token)]);
      setCleaners(cl);
      setApartments(apts);
    } catch (err: any) {
      setCleanerError(err.response?.data?.message || err.message || 'Failed to load cleaners');
    } finally {
      setCleanersLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (activeTab === 'cleaners' && !cleanersLoaded) {
      setCleanersLoaded(true);
      loadCleaners();
    }
  }, [activeTab, cleanersLoaded, loadCleaners]);

  const loadContacts = useCallback(async () => {
    setContactsLoading(true);
    setContactError(null);
    try {
      const data = await getMyContacts(token);
      setContacts(data);
    } catch (err: any) {
      setContactError(err.response?.data?.message || err.message || 'Failed to load contacts');
    } finally {
      setContactsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (activeTab === 'contacts' && !contactsLoaded) {
      setContactsLoaded(true);
      loadContacts();
    }
  }, [activeTab, contactsLoaded, loadContacts]);

  // ── Profile handlers ─────────────────────────────────────────────────────────
  const handleProfileSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSavingProfile(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await updateMyProfile({ firstName, lastName, phoneNumber, ico }, token);
      setEmail(updated.email || '');
      setFirstName(updated.firstName || '');
      setLastName(updated.lastName || '');
      setPhoneNumber(updated.phoneNumber || '');
      setIco(updated.ico || '');
      setSuccess(t.profileSaved);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to save profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (newPassword.length < 8) { setError(t.errorPasswordTooShort); return; }
    if (newPassword !== confirmPassword) { setError(t.errorPasswordMismatch); return; }
    setSavingPassword(true);
    try {
      await changeMyPassword({ currentPassword, newPassword }, token);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setSuccess(t.passwordChanged);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  };

  // ── TTLock handlers ──────────────────────────────────────────────────────────
  const handleTtlockConnect = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setTtlockConnecting(true);
    setTtlockError(null);
    setTtlockSuccess(null);
    try {
      const account = await connectTtlockAccount(token, ttlockUsername, MD5(ttlockPassword).toString());
      setTtlockAccount(account);
      setTtlockPassword('');
      setTtlockSuccess(t.ttlockConnected);
      setTtlockReconnectOpen(false);
      const locks = await getTtlockLocks(token);
      setTtlockLocks(locks);
    } catch (err: any) {
      setTtlockError(err.response?.data?.message || err.message || 'Failed to connect TTLock account');
    } finally {
      setTtlockConnecting(false);
    }
  };

  // ── Cleaner handlers ─────────────────────────────────────────────────────────
  const openCreateCleaner = () => {
    setEditingCleanerId(null);
    setCleanerForm(emptyCleanerForm());
    setShowCleanerForm(true);
    setCleanerError(null);
    setCleanerSuccess(null);
  };

  const openEditCleaner = (c: Cleaner) => {
    setEditingCleanerId(c.id);
    setCleanerForm({ name: c.name, scheduleDays: c.scheduleDays, apartmentIds: [...c.apartmentIds] });
    setShowCleanerForm(true);
    setCleanerError(null);
    setCleanerSuccess(null);
  };

  const cancelCleanerForm = () => { setShowCleanerForm(false); setEditingCleanerId(null); };

  const saveCleaner = async () => {
    if (!cleanerForm.name.trim()) { setCleanerError(t.cleanerNameRequired); return; }
    setSavingCleaner(true);
    setCleanerError(null);
    try {
      if (editingCleanerId != null) {
        await updateCleaner(editingCleanerId, cleanerForm, token);
        setCleanerSuccess(t.cleanerUpdated);
      } else {
        await createCleaner(cleanerForm, token);
        setCleanerSuccess(t.cleanerCreated);
      }
      setShowCleanerForm(false);
      await loadCleaners();
    } catch (err: any) {
      setCleanerError(err.response?.data?.message || err.message || 'Save failed');
    } finally {
      setSavingCleaner(false);
    }
  };

  const removeCleaner = async (id: number) => {
    if (!window.confirm(t.confirmDeleteCleaner)) return;
    try {
      await deleteCleaner(id, token);
      setCleaners(prev => prev.filter(c => c.id !== id));
      setCleanerSuccess(t.cleanerDeleted);
    } catch (err: any) {
      setCleanerError(err.response?.data?.message || err.message || 'Delete failed');
    }
  };

  const toggleCleanerApartment = (id: number) => {
    setCleanerForm(prev => ({
      ...prev,
      apartmentIds: prev.apartmentIds.includes(id)
        ? prev.apartmentIds.filter(a => a !== id)
        : [...prev.apartmentIds, id],
    }));
  };

  const copyCleanerUrl = (privateKey: string) => {
    const url = `${window.location.origin}/cleaning/${privateKey}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedKey(privateKey);
      setTimeout(() => setCopiedKey(null), 2000);
    });
  };

  const aptName = (id: number) => {
    const a = apartments.find(ap => ap.id === id);
    if (!a) return String(id);
    return (a as any).internalName?.trim() || a.displayName || a.name;
  };

  return (
    <div className="page-container" style={{ maxWidth: 900 }}>

      {/* Header */}
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>{t.pageTitle}</h2>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, borderBottom: '1px solid #e2e8f0', marginBottom: 16, paddingBottom: 10 }}>
        {(['profile', 'ttlock', 'integrations', 'cleaners', 'contacts', 'payment-methods', 'subscription', 'privacy'] as TabKey[]).map(tab => (
          <button
            key={tab}
            onClick={() => switchTab(tab)}
            style={{
              padding: '7px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
              fontWeight: 600, fontSize: '0.88rem',
              background: activeTab === tab ? 'var(--brand)' : 'var(--bg-page)',
              color: activeTab === tab ? '#fff' : 'var(--text-muted)',
              transition: 'background 0.15s ease',
            }}
          >
            {tabLabels[tab]}
          </button>
        ))}
      </div>

      {/* Tab: Profile */}
      {activeTab === 'profile' && (
        <div className="settings-tab-panel">
          <p style={{ color: '#4a5568', marginTop: 0 }}>
            {t.profileDescription}
          </p>

          {loading && <div>{t.loadingProfile}</div>}
          {error && <div style={{ color: '#c53030', marginBottom: 10 }}>{error}</div>}
          {success && <div style={{ color: '#2f855a', marginBottom: 10 }}>{success}</div>}

          {!loading && (
            <>
              <form onSubmit={handleProfileSave} style={{ marginBottom: 28 }}>
                <h3 style={{ marginBottom: 10 }}>{t.sectionPersonalInfo}</h3>
                <div style={formGridStyle}>
                  <div>
                    <label style={labelStyle}>{t.fieldEmailLogin}</label>
                    <input type="email" value={email} style={{ ...inputStyle, background: '#edf2f7' }} disabled />
                  </div>
                  <div>
                    <label style={labelStyle}>{t.fieldFirstName}</label>
                    <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>{t.fieldLastName}</label>
                    <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>{t.fieldTelephone}</label>
                    <input type="text" value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>{t.fieldIco}</label>
                    <input type="text" value={ico} onChange={e => setIco(e.target.value)} style={inputStyle} placeholder="12345678" />
                  </div>
                </div>
                <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end' }}>
                  <AppButton type="submit" variant="green" size="standard" disabled={savingProfile}>
                    {savingProfile ? t.savingProfileBtn : t.saveProfileBtn}
                  </AppButton>
                </div>
              </form>

              <div style={{ marginBottom: 28, borderTop: '1px solid #e2e8f0', paddingTop: 16 }}>
                <button
                  type="button"
                  onClick={() => setPasswordOpen(v => !v)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: 0,
                    marginBottom: passwordOpen ? 16 : 0,
                  }}
                >
                  <h3 style={{ margin: 0 }}>🔑 {t.sectionChangePassword}</h3>
                  <span style={{ color: '#718096', fontSize: '1.1rem' }}>{passwordOpen ? '▲' : '▼'}</span>
                </button>
                {passwordOpen && (
                  <form onSubmit={handlePasswordSave}>
                    <div style={formGridStyle}>
                      <div>
                        <label style={labelStyle}>{t.fieldCurrentPassword}</label>
                        <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} style={inputStyle} required />
                      </div>
                      <div>
                        <label style={labelStyle}>{t.fieldNewPassword}</label>
                        <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} style={inputStyle} minLength={8} required />
                      </div>
                      <div>
                        <label style={labelStyle}>{t.fieldConfirmPassword}</label>
                        <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} style={inputStyle} minLength={8} required />
                      </div>
                    </div>
                    <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end' }}>
                      <AppButton type="submit" variant="green" size="standard" disabled={savingPassword}>
                        {savingPassword ? t.changingPasswordBtn : t.changePasswordBtn}
                      </AppButton>
                    </div>
                  </form>
                )}
              </div>

              <div style={{ paddingTop: 20, borderTop: '1px solid #e2e8f0' }}>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#4a5568', marginBottom: 8 }}>
                    {tc.appLanguage}
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {LANGS.map(l => (
                      <button
                        key={l.code}
                        onClick={() => setLang(l.code)}
                        style={{
                          padding: '6px 14px',
                          borderRadius: 8,
                          border: `2px solid ${lang === l.code ? '#2563eb' : '#e2e8f0'}`,
                          background: lang === l.code ? '#eff6ff' : '#fff',
                          color: lang === l.code ? '#1d4ed8' : '#4a5568',
                          fontWeight: lang === l.code ? 700 : 400,
                          cursor: 'pointer',
                          fontSize: '0.88rem',
                        }}
                      >
                        {l.flag} {l.label}
                      </button>
                    ))}
                  </div>
                </div>
                <AppButton variant="red" size="standard" onClick={() => { clearStoredAuthState(); navigate('/login'); }}>
                  {tc.signOut}
                </AppButton>
              </div>
            </>
          )}
        </div>
      )}

      {/* Tab: Smart Lock */}
      {activeTab === 'ttlock' && (
        <div className="settings-tab-panel">
          <p style={{ color: '#4a5568', marginTop: 0 }}>
            {t.ttlockDescription}
          </p>

          {ttlockError && <div style={{ color: '#c53030', marginBottom: 10 }}>{ttlockError}</div>}
          {ttlockSuccess && <div style={{ color: '#2f855a', marginBottom: 10 }}>{ttlockSuccess}</div>}

          {ttlockLoading ? (
            <div style={{ color: '#718096' }}>{t.loadingProfile}</div>
          ) : (
            <>
              <div style={{ marginBottom: 20 }}>
                {ttlockAccount ? (
                  <TtlockAccountStatus account={ttlockAccount} />
                ) : (
                  <div style={{ padding: '10px 14px', background: '#fef9c3', border: '1px solid #fde047', borderRadius: 8, color: '#713f12', fontWeight: 500 }}>
                    {t.ttlockNoAccount}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: 24 }}>
                <button
                  type="button"
                  onClick={() => setTtlockReconnectOpen(v => !v)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: 0,
                    marginBottom: ttlockReconnectOpen ? 16 : 0,
                  }}
                >
                  <h3 style={{ margin: 0 }}>{ttlockAccount ? t.ttlockReconnectTitle : t.ttlockConnectTitle}</h3>
                  <span style={{ color: '#718096', fontSize: '1.1rem' }}>{ttlockReconnectOpen ? '▲' : '▼'}</span>
                </button>
                {ttlockReconnectOpen && (
                  <form onSubmit={handleTtlockConnect}>
                    <div style={formGridStyle}>
                      <div>
                        <label style={labelStyle}>{t.ttlockUsernameLabel}</label>
                        <input type="text" value={ttlockUsername} onChange={e => setTtlockUsername(e.target.value)} style={inputStyle} placeholder="your@email.com" required />
                      </div>
                      <div>
                        <label style={labelStyle}>{t.ttlockPasswordLabel}</label>
                        <input type="password" value={ttlockPassword} onChange={e => setTtlockPassword(e.target.value)} style={inputStyle} required />
                      </div>
                    </div>
                    <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end' }}>
                      <AppButton type="submit" variant="green" size="standard" disabled={ttlockConnecting}>
                        {ttlockConnecting ? t.connectingBtn : t.connectBtn}
                      </AppButton>
                    </div>
                  </form>
                )}
              </div>

              {ttlockAccount && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <h3 style={{ margin: 0 }}>{t.locksTitle(ttlockLocks.length)}</h3>
                  </div>
                  {ttlockLocks.length === 0 ? (
                    <div style={{ color: '#718096', fontSize: '0.9rem' }}>{t.noLocksFound}</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {ttlockLocks.map(lock => (
                        <div key={lock.id} className="ttlock-lock-card">
                          <div className="ttlock-lock-info">
                            <div style={{ fontWeight: 600 }}>{lock.lockName}{lock.lockAlias ? ` (${lock.lockAlias})` : ''}</div>
                            <div style={{ fontSize: '0.8rem', color: '#718096' }}>
                              ID: {lock.lockId} · PIN: {lock.pinLength} digits · Allowed: {lock.allowedDigits}
                              {lock.electricQuantity != null && ` · 🔋 ${lock.electricQuantity}%`}
                            </div>
                          </div>
                          <span className="ttlock-lock-badge" style={{ fontSize: '0.75rem', fontWeight: 600, padding: '2px 8px', borderRadius: 12, background: lock.isActive ? '#dcfce7' : '#fee2e2', color: lock.isActive ? '#166534' : '#991b1b' }}>
                            {lock.isActive ? t.lockActive : t.lockInactive}
                          </span>
                          <div className="ttlock-lock-action">
                            <AppButton variant="blue" size="standard" onClick={() => navigate(`/locks/${lock.lockId}`)}>
                              {t.configureBtn}
                            </AppButton>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* Tab: Cleaners */}
      {activeTab === 'cleaners' && (
        <div className="settings-tab-panel">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <p style={{ color: '#4a5568', margin: 0 }}>
              {t.cleanersDescription}
            </p>
            {!showCleanerForm && (
              <AppButton variant="green" size="standard" onClick={openCreateCleaner}>{t.addCleanerBtn}</AppButton>
            )}
          </div>

          {cleanerError && <div style={{ color: '#991b1b', background: '#fef2f2', border: '1px solid #fecaca', padding: '10px 14px', borderRadius: 8, marginBottom: 12 }}>{cleanerError}</div>}
          {cleanerSuccess && <div style={{ color: '#276749', background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '10px 14px', borderRadius: 8, marginBottom: 12 }}>{cleanerSuccess}</div>}

          {/* Cleaner form */}
          {showCleanerForm && (
            <div className="settings-pad-lg" style={{ background: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: 24 }}>
              <h3 style={{ marginTop: 0, marginBottom: 16 }}>{editingCleanerId != null ? t.editCleanerTitle : t.newCleanerTitle}</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14, marginBottom: 16 }}>
                <div>
                  <label style={labelStyle}>{t.cleanerNameLabel}</label>
                  <input style={inputStyle} value={cleanerForm.name} onChange={e => setCleanerForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Jana Novák" />
                </div>
                <div>
                  <label style={labelStyle}>{t.cleanerScheduleDaysLabel}</label>
                  <input style={inputStyle} type="number" min={1} max={365} value={cleanerForm.scheduleDays}
                    onChange={e => setCleanerForm(f => ({ ...f, scheduleDays: parseInt(e.target.value) || 30 }))} />
                  <div style={{ fontSize: '0.75rem', color: '#718096', marginTop: 3 }}>{t.cleanerScheduleDaysHint}</div>
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>{t.cleanerApartmentsLabel}</label>
                {apartments.length === 0 ? (
                  <div style={{ color: '#718096', fontSize: '0.9rem' }}>{t.noApartmentsFound}</div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {apartments.map(a => {
                      const name = (a as any).internalName?.trim() || a.displayName || a.name;
                      const checked = cleanerForm.apartmentIds.includes(a.id!);
                      return (
                        <label key={a.id} style={{
                          display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                          padding: '6px 12px', borderRadius: 20, fontSize: '0.875rem', fontWeight: 600,
                          background: checked ? '#dbeafe' : '#f0f4f8',
                          border: `1px solid ${checked ? '#93c5fd' : '#e2e8f0'}`,
                          color: checked ? '#1e40af' : '#4a5568',
                        }}>
                          <input type="checkbox" checked={checked} onChange={() => toggleCleanerApartment(a.id!)}
                            style={{ display: 'none' }} />
                          {name}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <AppButton variant="green" size="standard" onClick={saveCleaner} disabled={savingCleaner}>
                  {savingCleaner ? t.savingBtn : editingCleanerId != null ? t.updateBtn : t.createBtn}
                </AppButton>
                <button className="app-btn app-btn-standard" onClick={cancelCleanerForm} disabled={savingCleaner}
                  style={{ background: '#edf2f7', color: '#2d3748' }}>{tc.cancel}</button>
              </div>
            </div>
          )}

          {/* Cleaner list */}
          {cleanersLoading ? (
            <div style={{ color: '#718096', textAlign: 'center', padding: 32 }}>{t.loadingCleaners}</div>
          ) : cleaners.length === 0 && !showCleanerForm ? (
            <div style={{ color: '#718096', textAlign: 'center', padding: 40, fontStyle: 'italic' }}>
              {t.noCleaners}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {cleaners.map(c => {
                const url = `${window.location.origin}/cleaning/${c.privateKey}`;
                return (
                  <div key={c.id} className="settings-pad-md" style={{ border: '1px solid #e2e8f0', borderRadius: 10, background: '#fff' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>{c.name}</div>
                        <div style={{ fontSize: '0.8rem', color: '#718096', marginTop: 2 }}>
                          {c.scheduleDays} {t.daysVisible}
                          {c.apartmentIds.length > 0
                            ? ' · ' + c.apartmentIds.map(aptName).join(', ')
                            : ' · ' + t.noApartmentsAssigned}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <AppButton variant="blue" size="standard" onClick={() => openEditCleaner(c)}
                          style={{ padding: '5px 14px', fontSize: '0.85rem' }}>{tc.edit}</AppButton>
                        <AppButton variant="red" size="standard" onClick={() => removeCleaner(c.id)}
                          style={{ padding: '5px 14px', fontSize: '0.85rem' }}>{tc.delete}</AppButton>
                      </div>
                    </div>
                    <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <div style={{
                        flex: 1, minWidth: 0, padding: '7px 12px', background: '#f7fafc',
                        border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.8rem',
                        color: '#4a5568', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {url}
                      </div>
                      <button
                        className={`app-btn app-btn-standard ${copiedKey === c.privateKey ? 'app-btn-green' : 'app-btn-blue'}`}
                        onClick={() => copyCleanerUrl(c.privateKey)}
                        style={{ padding: '7px 14px', fontSize: '0.85rem', flexShrink: 0 }}
                      >
                        {copiedKey === c.privateKey ? t.copiedBtn : t.copyLinkBtn}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Tab: Contacts */}
      {activeTab === 'contacts' && (
        <div className="settings-tab-panel">
          <div className="settings-panel-header">
            <p style={{ color: '#4a5568', margin: 0 }}>
              {t.contactsDescription}
            </p>
            {!showContactForm && (
              <AppButton variant="green" size="standard" onClick={() => { setContactForm({ name: '', phoneNumber: '', email: '' }); setEditingContactId(null); setShowContactForm(true); setContactError(null); setContactSuccess(null); }}>{t.addContactBtn}</AppButton>
            )}
          </div>

          {contactError && <div style={{ color: '#991b1b', background: '#fef2f2', border: '1px solid #fecaca', padding: '10px 14px', borderRadius: 8, marginBottom: 12 }}>{contactError}</div>}
          {contactSuccess && <div style={{ color: '#276749', background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '10px 14px', borderRadius: 8, marginBottom: 12 }}>{contactSuccess}</div>}

          {showContactForm && (
            <div className="settings-pad-lg" style={{ background: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: 24 }}>
              <h3 style={{ marginTop: 0, marginBottom: 16 }}>{editingContactId != null ? t.editContactTitle : t.newContactTitle}</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14, marginBottom: 16 }}>
                <div>
                  <label style={labelStyle}>{t.contactNameLabel}</label>
                  <input style={inputStyle} value={contactForm.name} onChange={e => setContactForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Jan Novák" />
                </div>
                <div>
                  <label style={labelStyle}>{t.contactPhoneLabel}</label>
                  <input style={inputStyle} value={contactForm.phoneNumber || ''} onChange={e => setContactForm(f => ({ ...f, phoneNumber: e.target.value }))} placeholder="+420 777 000 000" />
                </div>
                <div>
                  <label style={labelStyle}>{t.contactEmailLabel}</label>
                  <input style={inputStyle} type="email" value={contactForm.email || ''} onChange={e => setContactForm(f => ({ ...f, email: e.target.value }))} placeholder="contact@example.com" />
                </div>
                <div>
                  <label style={labelStyle}>{t.contactCompanyLabel}</label>
                  <input style={inputStyle} value={contactForm.companyName || ''} onChange={e => setContactForm(f => ({ ...f, companyName: e.target.value }))} />
                </div>
                <div>
                  <label style={labelStyle}>{t.contactIcoLabel}</label>
                  <input style={inputStyle} value={contactForm.ico || ''} onChange={e => setContactForm(f => ({ ...f, ico: e.target.value }))} placeholder="12345678" />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>{t.contactAddressLabel}</label>
                  <input style={inputStyle} value={contactForm.address || ''} onChange={e => setContactForm(f => ({ ...f, address: e.target.value }))} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <AppButton variant="green" size="standard" disabled={savingContact || !contactForm.name.trim()} onClick={async () => {
                  setSavingContact(true); setContactError(null); setContactSuccess(null);
                  try {
                    if (editingContactId != null) {
                      await updateContact(editingContactId, contactForm, token);
                      setContactSuccess(t.contactUpdated);
                    } else {
                      await createContact(contactForm, token);
                      setContactSuccess(t.contactCreated);
                    }
                    setShowContactForm(false);
                    await loadContacts();
                  } catch (err: any) {
                    setContactError(err.response?.data?.message || err.message || 'Failed to save contact');
                  } finally { setSavingContact(false); }
                }}>{savingContact ? t.savingBtn : t.saveBtn}</AppButton>
                <AppButton size="standard" onClick={() => setShowContactForm(false)}>{tc.cancel}</AppButton>
              </div>
            </div>
          )}

          {contactsLoading ? (
            <div style={{ color: '#718096', padding: '20px 0' }}>{t.loadingContacts}</div>
          ) : contacts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 24px', background: '#f7fafc', borderRadius: 10, border: '1px dashed #cbd5e0' }}>
              <div style={{ fontSize: '2.4rem', marginBottom: 12 }}>📋</div>
              <div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#2d3748', marginBottom: 8 }}>{t.noContacts}</div>
              <p style={{ color: '#718096', fontSize: '0.9rem', maxWidth: 400, margin: '0 auto 20px', lineHeight: 1.6 }}>{t.contactsEmptyBody}</p>
              <button
                onClick={() => { setContactForm({ name: '', phoneNumber: '', email: '' }); setEditingContactId(null); setShowContactForm(true); setContactError(null); setContactSuccess(null); }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#38a169', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 700, cursor: 'pointer', fontSize: '0.95rem' }}
              >
                {t.addContactBtn}
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {contacts.map(c => (
                <div key={c.id} className="settings-pad-sm" style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#f7fafc', borderRadius: 8, border: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 150 }}>
                    <div style={{ fontWeight: 600, color: '#1a202c' }}>{c.name}</div>
                    <div style={{ fontSize: '0.83rem', color: '#718096' }}>
                      {[c.phoneNumber, c.email].filter(Boolean).join(' · ') || t.noContactDetails}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <AppButton variant="blue" size="compact" onClick={() => { setContactForm({ name: c.name, phoneNumber: c.phoneNumber, email: c.email, companyName: c.companyName, ico: c.ico, address: c.address }); setEditingContactId(c.id!); setShowContactForm(true); setContactError(null); setContactSuccess(null); }}>{tc.edit}</AppButton>
                    <AppButton variant="red" size="compact" onClick={async () => {
                      if (!window.confirm(t.confirmDeleteContact(c.name))) return;
                      setContactError(null);
                      try {
                        await deleteContact(c.id!, token);
                        await loadContacts();
                      } catch (err: any) {
                        setContactError(err.response?.data?.message || err.message || 'Failed to delete contact');
                      }
                    }}>{tc.delete}</AppButton>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tab: Channel manager (Hostex and future PMS connections) */}
      {activeTab === 'integrations' && (
        <div className="settings-tab-panel">
          <ChannelManagerHub />
        </div>
      )}

      {/* Tab: Payment Methods */}
      {activeTab === 'payment-methods' && (
        <div className="settings-tab-panel">
          <SettingsPaymentMethods />
        </div>
      )}

      {/* Tab: Subscription */}
      {activeTab === 'subscription' && (
        <div className="settings-tab-panel">
          <Subscription />
        </div>
      )}

      {/* Tab: Privacy. Only links out: the management page stays the one place with withdrawal logic. */}
      {activeTab === 'privacy' && (
        <div className="settings-tab-panel">
          <h3 style={{ marginTop: 0 }}>{t.privacyTitle}</h3>
          <p style={{ color: '#4a5568' }}>{t.privacyDesc}</p>
          <Link to="/privacy/measurement" className="app-btn app-btn-blue app-btn-standard" style={{ textDecoration: 'none', display: 'inline-flex' }}>{t.privacyManageLink}</Link>
        </div>
      )}
    </div>
  );
};

export default ManagerProfile;
